/**
 * 等级码 → 中文显示名。
 *
 * <p>⚠️ 6/7/8 是 2026-10-13 新增的**会员档位**，10-16 原型改名为 入门版/专业版/专业直播版
 * （旧称 5/50/100 条档会员），与后端 {@code UserLevelConstants} 保持一致。
 * 旧 1–5 是已退役的订阅档，保留仅为兼容历史数据。</p>
 */
export const USER_LEVEL_LABELS: Record<string, string> = {
    "0": "游客",
    "1": "免费体验版",
    "2": "个人版",
    "3": "创作者版",
    "4": "团队版",
    "5": "旗舰版",
    "6": "入门版",
    "7": "专业版",
    "8": "专业直播版",
};

/**
 * 走**共享豆池**的等级（后端 {@code UserLevelConstants#isEnterprise}）。
 *
 * <p>6/7/8 一并纳入：B 方案下 50/100 档会建会员组（复用 nw_tenant），
 * 此时用户的 {@code role} 会被升为管理员，池路由才走得通。</p>
 */
export const ENTERPRISE_LEVELS = ["4", "5", "6", "7", "8"] as const;

export function levelLabel(level?: string | null): string {
    if (!level) return "游客";
    return USER_LEVEL_LABELS[level] || level;
}

/**
 * 根据等级配置列表动态生成企业版下拉选项
 * 使用人数从 nw_member_level_config 表获取，不做硬编码
 */
export interface LevelOption {
    value: string;
    label: string;
}

export function buildEnterpriseLevelOptions(
    configs: { levelCode: string; levelName: string; userCount: number }[]
): LevelOption[] {
    return ENTERPRISE_LEVELS
        .map((code) => {
            const cfg = configs.find((c) => c.levelCode === code);
            if (cfg) {
                return { value: code, label: `${cfg.levelName}（最多 ${cfg.userCount} 人）` };
            }
            return { value: code, label: levelLabel(code) };
        });
}

/**
 * @deprecated 使用 buildEnterpriseLevelOptions() 从 API 动态获取
 * 保留作为 fallback，当 API 不可用时使用
 */
export const ENTERPRISE_LEVEL_OPTIONS: LevelOption[] = [
    { value: "4", label: "团队版（最多 3 人）" },
    { value: "5", label: "旗舰版（最多 8 人）" },
];

/**
 * @deprecated 使用等级配置 API 的 userCount 字段
 * 保留作为 fallback
 */
export function maxMembers(level?: string | null): number {
    if (level === "4") return 3;
    if (level === "5") return 8;
    return 1;
}

export function isEnterpriseLevel(level?: string | null): boolean {
    return (ENTERPRISE_LEVELS as readonly string[]).includes(level ?? "");
}
